/* global gdAlertify */
import React, { useState } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatDate, formatMoney } from "../utils/utils";
import CobranzaDetalle from "./CobranzaDetalle";
import CobranzaAjusteModal from "./CobranzaAjusteModal";
import CobranzaAdelantoModal from "./CobranzaAdelantoModal";
import CobranzaFacturas from "./CobranzaFacturas";
import CartolaPacienteModal from "./components/CartolaPacienteModal";
import CobranzaReajusteModal from "./CobranzaReajusteModal";
import NotificarCobranzaModal from "./MotorEnvios/NotificarCobranzaModal";
import MonthInput from "./components/MonthInput";

const Cobranza = () => {
    const [fecha, setFecha] = useState("");
    const [resultados, setResultados] = useState([]);
    const [vistaActiva, setVistaActiva] = useState("general");

    const [isLiquidado, setIsLiquidado] = useState(false);

    const [showDetalle, setShowDetalle] = useState(false);
    const [showAjusteModal, setShowAjusteModal] = useState(false);
    const [showAdelantoModal, setShowAdelantoModal] = useState(false);
    const [showCartolaModal, setShowCartolaModal] = useState(false);
    const [showReajusteModal, setShowReajusteModal] = useState(false);
    const [reajustePacientes, setReajustePacientes] = useState([]);
    const [selectedPacienteCartola, setSelectedPacienteCartola] = useState(null);
    const [detallePacienteID, setDetallePacienteID] = useState(null);
    const [detallePacienteNombre, setDetallePacienteNombre] = useState("");
    const [detalleCargos, setDetalleCargos] = useState(0);
    const [detalleDescuentos, setDetalleDescuentos] = useState(0);
    const [detalleObservacionAjuste, setDetalleObservacionAjuste] = useState("");
    const [modoNotificacion, setModoNotificacion] = useState(false);
    const [totalPagar, setTotalPagar] = useState(0);
    const [regenerando, setRegenerando] = useState(false);
    const [reliquidandoPacienteID, setReliquidandoPacienteID] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showNotificarCorreo, setShowNotificarCorreo] = useState(false);

    const buscarCobranza = async () => {
        if (!fecha) return;
        const [anio, mes] = fecha.split("-");
        gdAlertify.showLoader();
        setLoading(true);
        try {
            const params = { fecha };
            const response = await axiosInstance.get("/acuerdo-mutuo/cobranza", { params });
            setResultados(response.data.data);
            setIsLiquidado(response.data.liquidado || false);
        } catch (error) {
            console.error("Error al buscar cobranza:", error);
        } finally {
            gdAlertify.hideLoader();
            setLoading(false);
        }
    };

    // Reliquida todos los pacientes del mes ya liquidado con las reglas y datos actuales
    // (ej. recargos del periodo que antes no se cobraban a la familia).
    const reliquidarMes = () => {
        if (!fecha || !isLiquidado) return;
        gdAlertify.confirm({
            title: `¿Reliquidar el periodo ${fecha}?`,
            message: "Se recalcula el cobro de todos los pacientes de este mes con los datos y reglas actuales (turnos, valores, recargos del periodo y ajustes) y se rehacen los saldos. Los abonos registrados se mantienen.",
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    const res = await axiosInstance.post('/acuerdo-mutuo/cobranza/reliquidar-mes', { periodo: fecha });
                    if (res.data.error) throw new Error(res.data.message);
                    const d = res.data.data || {};
                    const lineas = [
                        `Pacientes reliquidados: ${d.reliquidados ?? 0}`,
                        `Total del mes: $${formatMoney(d.total_antes ?? 0)} → $${formatMoney(d.total_despues ?? 0)}`,
                    ];
                    if ((d.errores || []).length) {
                        lineas.push(`Con error (${d.errores.length}): ${d.errores.map(e => `${e.nombre || e.paciente_id}: ${e.error}`).join("; ")}`);
                    }
                    if ((d.no_incluidos || []).length) {
                        lineas.push(`Con cobro pero fuera de la liquidación (no se agregaron): ${d.no_incluidos.map(x => x.nombre || x.paciente_id).join(", ")}`);
                    }
                    alert(`Reliquidación del periodo ${fecha}:\n\n${lineas.join("\n")}`);
                    await buscarCobranza();
                } catch (e) {
                    gdAlertify.notiError(e.response?.data?.message || e.message || "No se pudo reliquidar el periodo.");
                } finally {
                    gdAlertify.hideLoader();
                }
            },
            onCancel: () => {}
        });
    };

    const regenerarDetalle = async () => {
        if (!fecha || !isLiquidado) return;
        const confirmar = window.confirm(
            `¿Regenerar el detalle del periodo ${fecha}?\n\nEsto calculará el snapshot (turnos y adicionales) para los pacientes que no lo tengan guardado.\nLos que ya tienen detalle NO se tocarán.`
        );
        if (!confirmar) return;
        setRegenerando(true);
        try {
            const res = await axiosInstance.post('/acuerdo-mutuo/cobranza/detalle/regenerar', { periodo: fecha });
            const d = res.data.data || {};
            const lines = [
                `✅ Generados: ${d.generados ?? 0}`,
                `⏭️ Omitidos (ya tenían detalle): ${d.omitidos ?? 0}`,
                `❌ Errores: ${d.errores ?? 0}`,
            ];
            alert(`Regeneración completada para ${fecha}:\n\n${lines.join('\n')}`);
            buscarCobranza();
        } catch (e) {
            alert('Error al regenerar: ' + (e.response?.data?.message || e.message));
        } finally {
            setRegenerando(false);
        }
    };

    const reliquidarPaciente = (row) => {
        if (!fecha || !isLiquidado || !row?.paciente_id) return;

        gdAlertify.confirm({
            title: `¿Reliquidar ${row.nombre_paciente}?`,
            message: "Se recalculará solo este paciente usando los turnos y adicionales actuales. El periodo seguirá cerrado.",
            onConfirm: async () => {
                setReliquidandoPacienteID(row.paciente_id);
                try {
                    const resp = await axiosInstance.post('/acuerdo-mutuo/cobranza/detalle/reliquidar-paciente', {
                        periodo: fecha,
                        paciente_id: row.paciente_id
                    });

                    if (resp.data?.error) {
                        throw new Error(resp.data.message || "No se pudo reliquidar el paciente.");
                    }

                    gdAlertify.notiSuccess(resp.data?.message || "Paciente reliquidado correctamente.");
                    buscarCobranza();
                } catch (e) {
                    gdAlertify.notiError(e.response?.data?.message || e.message || "No se pudo reliquidar el paciente.");
                } finally {
                    setReliquidandoPacienteID(null);
                }
            },
            onCancel: () => {}
        });
    };

    const columns = [
        {
            title: "Acciones",
            name: "acciones",
            render: row => (
                <div className="dropdown">
                    <button
                        className="btn btn-sm btn-secondary dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        Acciones
                    </button>
                    <ul className="dropdown-menu">
                        <li>
                            <button 
                                className="dropdown-item text-primary"
                                onClick={() => {
                                    setDetallePacienteID(row.paciente_id);
                                    setDetallePacienteNombre(row.nombre_paciente);
                                    setTotalPagar(
                                        (Number(row.total_general) || 0) + 
                                        (Number(row.saldo_anterior) || 0) + 
                                        (Number(row.otros_suma) || 0) - 
                                        (Number(row.otros_resta) || 0)
                                    );
                                    setModoNotificacion(false);
                                    setShowDetalle(true);
                                }}
                            >
                                <i className="bi bi-eye me-2"></i> Ver Detalle
                            </button>
                        </li>
                        <li>
                            <button 
                                className="dropdown-item text-success"
                                onClick={() => {
                                    setDetallePacienteID(row.paciente_id);
                                    setDetallePacienteNombre(row.nombre_paciente);
                                    setTotalPagar(
                                        (Number(row.total_general) || 0) + 
                                        (Number(row.saldo_anterior) || 0) + 
                                        (Number(row.otros_suma) || 0) - 
                                        (Number(row.otros_resta) || 0)
                                    );
                                    setModoNotificacion(true);
                                    setShowDetalle(true);
                                }}
                            >
                                <i className="bi bi-envelope me-2"></i> Notificar al Paciente
                            </button>
                        </li>

                        <li>
                            <button 
                                className="dropdown-item text-warning text-dark mt-1"
                                onClick={() => {
                                    setDetallePacienteID(row.paciente_id);
                                    setDetallePacienteNombre(row.nombre_paciente);
                                    setDetalleCargos(Number(row.otros_suma) || 0);
                                    setDetalleDescuentos(Number(row.otros_resta) || 0);
                                    setDetalleObservacionAjuste(row.observacion || "");
                                    setShowAjusteModal(true);
                                }}
                                disabled={isLiquidado}
                            >
                                <i className="bi bi-sliders me-2"></i> {isLiquidado ? "Ajustes (Desactivado)" : "Ajustes (Suma/Resta)"}
                            </button>
                        </li>
                        <li>
                            <button
                                className="dropdown-item text-secondary mt-1"
                                onClick={() => {
                                    setDetallePacienteID(row.paciente_id);
                                    setDetallePacienteNombre(row.nombre_paciente);
                                    setShowAdelantoModal(true);
                                }}
                                disabled={isLiquidado && row.estado_paciente == 1}
                            >
                                <i className="bi bi-calendar-check me-2"></i> {isLiquidado && row.estado_paciente == 1 ? "Adelantos (Desactivado)" : "Adelantos (Sig. Mes)"}
                            </button>
                        </li>
                        <li>
                            <button 
                                className="dropdown-item text-info mt-1 fw-bold"
                                onClick={() => {
                                    setSelectedPacienteCartola(row);
                                    setShowCartolaModal(true);
                                }}
                            >
                                <i className="bi bi-person-lines-fill me-2"></i> Ver Cartola
                            </button>
                        </li>
                        {isLiquidado && (
                            <li>
                                <button
                                    className="dropdown-item text-danger mt-1 fw-bold"
                                    onClick={() => reliquidarPaciente(row)}
                                    disabled={reliquidandoPacienteID === row.paciente_id}
                                >
                                    {reliquidandoPacienteID === row.paciente_id ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Reliquidando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-arrow-repeat me-2"></i> Reliquidar Paciente
                                        </>
                                    )}
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            )
        },
        { title: "RUT", name: "rut" },
        { title: "Paciente", name: "nombre_paciente" },
        {
            title: "Estado",
            name: "estado_paciente",
            render: row => (
                <div className="d-flex flex-column align-items-start">
                    {row.estado_paciente == 1 ? (
                        <span className="badge bg-success">Activo</span>
                    ) : (
                        <span className="badge bg-danger">Retirado</span>
                    )}
                    {row.estado_paciente != 1 && row.fecha_retiro && (
                        <small className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                            Retiro: {formatDate(row.fecha_retiro)}
                        </small>
                    )}
                </div>
            )
        },
        {
            title: "Aviso Reajuste",
            name: "reajuste_apto_aviso",
            cellClassName: "text-center",
            headerClassName: "text-center",
            render: row => {
                if (row.reajuste_estado === "aplicar") {
                    return (
                        <span
                            className="badge bg-success"
                            title={row.fecha_reajuste_a_avisar ? `Reajuste previsto para ${formatDate(row.fecha_reajuste_a_avisar)}` : ""}
                        >
                            Aplicar reajuste
                        </span>
                    );
                }

                if (row.reajuste_avisado) {
                    return (
                        <span className="badge bg-success">
                            Notificado {row.fecha_aviso_reajuste ? formatDate(row.fecha_aviso_reajuste) : ""}
                        </span>
                    );
                }

                if (row.reajuste_apto_aviso) {
                    return (
                        <span
                            className="badge bg-warning text-dark"
                            title={row.fecha_reajuste_a_avisar ? `Reajuste previsto para ${formatDate(row.fecha_reajuste_a_avisar)}` : ""}
                        >
                            Notificar
                        </span>
                    );
                }

                return <span className="text-muted small">-</span>;
            }
        },
        {
            title: "Saldo Anterior",
            name: "saldo_anterior",
            type: "number",
            render: row => {
                const saldo = Number(row.saldo_anterior) || 0;
                if (saldo === 0) return <span className="text-muted fw-bold">$0</span>;
                return (
                    <span className={`${saldo < 0 ? "text-primary" : "text-warning"} fw-bold`}>
                        {saldo < 0 ? "-" : ""}${formatMoney(Math.abs(saldo))}
                    </span>
                );
            }
        },
        { 
            title: "Otros Suma", 
            name: "otros_suma", 
            type: "number",
            render: row => <span className="text-danger">{Number(row.otros_suma) > 0 ? `$${formatMoney(row.otros_suma)}` : '$0'}</span>
        },
        { 
            title: "Otros Resta", 
            name: "otros_resta", 
            type: "number",
            render: row => <span className="text-success">{Number(row.otros_resta) > 0 ? `$${formatMoney(row.otros_resta)}` : '$0'}</span>
        },
        { 
            title: "Observación", 
            name: "observacion", 
            render: row => <span className="text-muted small">{row.observacion || '-'}</span>
        },
        {
            title: "Total a Facturar Mes",
            name: "total_facturar_mes",
            type: "number",
            render: row => {
                const montoAdelantos = (row.turnos_siguiente_periodo || []).reduce(
                    (sum, t) => sum + (Number(t.monto) || 0), 0
                );
                const montoFacturarPeriodoAnterior = (row.turnos_facturar_periodo_anterior || []).reduce(
                    (sum, t) => sum + (Number(t.monto) || 0), 0
                );
                const total = (Number(row.total_general) || 0) + (Number(row.otros_suma) || 0) - (Number(row.otros_resta) || 0) - montoAdelantos + montoFacturarPeriodoAnterior;
                return (
                    <span className="fw-bold fs-6">
                        {`$${formatMoney(total)}`}
                        {row.turnos_facturar_periodo_anterior && row.turnos_facturar_periodo_anterior.length > 0 && (
                            <i
                                className="bi bi-info-circle text-primary ms-2"
                                title={`Incluye ${row.turnos_facturar_periodo_anterior.length} turno(s) ya cobrado(s) el mes anterior`}
                            ></i>
                        )}
                    </span>
                );
            }
        },
        {
            title: "Total a Cobrar",
            name: "deuda_total_computada",
            type: "number",
            render: row => {
                const total = (Number(row.total_general) || 0) + (Number(row.otros_suma) || 0) - (Number(row.otros_resta) || 0);
                return <span className="fw-bold fs-6">{`$${formatMoney(total)}`}</span>;
            }
        },
        { 
            title: "N° Notificación", 
            name: "numero_notificacion", 
            cellClassName: "text-center",
            headerClassName: "text-center",
            render: row => row.numero_notificacion ? `${row.numero_notificacion}` : "Sin Notificar" 
        },
        {   title: "Última Notificación",
            name: "fecha_ultima_notificacion",
            cellClassName: "text-center",
            headerClassName: "text-center",
            render: row => row.fecha_ultima_notificacion ? formatDate(row.fecha_ultima_notificacion) : "-" 
        },
        { 
            title: "N° Turnos", 
            name: "cantidad_turnos",
            type: "number",
            render: row => (
                <div className="d-flex align-items-center">
                    {row.cantidad_turnos}
                    {row.turnos_siguiente_periodo && row.turnos_siguiente_periodo.length > 0 && (
                        <i className="bi bi-info-circle text-primary ms-2" title={`Incluye ${row.turnos_siguiente_periodo.length} turnos(s) adelantado(s)`}></i>
                    )}
                </div>
            )
        },
        {
            title: "Total Turno",
            name: "total_turno",
            type: "number",
            render: row => {
                const total = (Number(row.valor_turnos_total) || 0) +
                              (Number(row.adicionales_turno_total) || 0) +
                              (Number(row.recargo_fs_turno_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Total Colación",
            name: "total_colacion",
            type: "number",
            render: row => {
                const total = (Number(row.colacion_total) || 0) +
                              (Number(row.adicionales_colacion_total) || 0) +
                              (Number(row.recargo_fs_colacion_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Total Movilización",
            name: "total_locomocion",
            type: "number",
            render: row => {
                const total = (Number(row.locomocion_total) || 0) +
                              (Number(row.adicionales_locomocion_total) || 0) +
                              (Number(row.recargo_fs_locomocion_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Otros / Extras",
            name: "total_otros",
            type: "number",
            render: row => {
                const total = (Number(row.otro_total) || 0) +
                              (Number(row.adicionales_otro_total) || 0) +
                              (Number(row.recargo_fs_otro_total) || 0) +
                              (Number(row.horas_extras_total) || 0) +
                              (Number(row.adicionales_horas_extras_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Total Adicionales",
            name: "total_adicionales_solo",
            type: "number",
            render: row => {
                const total = (Number(row.adicionales_turno_total) || 0) +
                              (Number(row.adicionales_colacion_total) || 0) +
                              (Number(row.adicionales_locomocion_total) || 0) +
                              (Number(row.adicionales_otro_total) || 0) +
                              (Number(row.adicionales_horas_extras_total) || 0) +
                              (Number(row.recargo_fs_turno_total) || 0) +
                              (Number(row.recargo_fs_colacion_total) || 0) +
                              (Number(row.recargo_fs_locomocion_total) || 0) +
                              (Number(row.recargo_fs_otro_total) || 0);
                return <span className="fw-bold text-primary">{total > 0 ? `$${formatMoney(total)}` : '$0'}</span>;
            }
        },
        { 
            title: "Total Deuda", 
            name: "saldo_cartola", 
            type: "number",
            render: row => (
                <span className={`fw-bold ${Number(row.saldo_cartola) > 0 ? 'text-danger' : 'text-primary'}`}>
                    {`$${formatMoney(row.saldo_cartola || 0)}`}
                </span>
            )
        }
    ];

    const resultadosFiltrados = resultados;
    const pendientesReajuste = resultados.filter(r => r.reajuste_estado === "aplicar");

    return (
        <div className="container-fluid mt-4">
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h3 className="mb-0">Cobranza</h3>
                        <div className="d-flex align-items-center gap-2">
                            {pendientesReajuste.length > 0 && (
                                <button
                                    className="btn btn-success btn-sm fw-bold"
                                    onClick={() => {
                                        setReajustePacientes(pendientesReajuste);
                                        setShowReajusteModal(true);
                                    }}
                                    title="Aplica los valores de reajuste semestral programados para los pacientes cuyo ciclo ya vencio"
                                >
                                    <i className="bi bi-arrow-up-circle me-2"></i>
                                    Aplicar reajuste ({pendientesReajuste.length})
                                </button>
                            )}
                            {isLiquidado && resultados.some(r => !r.tiene_detalle) && (
                                <button
                                    className="btn btn-outline-warning btn-sm fw-bold"
                                    onClick={regenerarDetalle}
                                    disabled={regenerando || !fecha}
                                    title="Regenera el detalle de turnos para pacientes que no lo tienen guardado (cobranzas antiguas)"
                                >
                                    {regenerando
                                        ? (<><span className="spinner-border spinner-border-sm me-2" role="status"></span>Regenerando...</>)
                                        : (<><i className="bi bi-arrow-repeat me-2"></i>Regenerar Detalle ({resultados.filter(r => !r.tiene_detalle).length})</> )
                                    }
                                </button>
                            )}
                            {isLiquidado && resultados.length > 0 && (
                                <button
                                    className="btn btn-primary btn-sm fw-bold"
                                    onClick={() => setShowNotificarCorreo(true)}
                                    title="Encola en el Motor de Envíos un correo por paciente con su detalle en PDF"
                                >
                                    <i className="bi bi-envelope-paper me-2"></i>
                                    Notificar por correo (masivo)
                                </button>
                            )}
                            {isLiquidado && (
                                <span className="badge bg-danger rounded-pill px-3 py-2 fs-6 shadow-sm">
                                    <i className="bi bi-lock-fill me-2"></i>
                                    Periodo Cerrado / Liquidado
                                </span>
                            )}
                        </div>
                    </div>
                    
                    <div className="row g-3 align-items-end">
                        <div className="col-md-6">
                            <label className="form-label">Mes</label>
                            <MonthInput
                                className="form-control"
                                value={fecha}
                                onChange={e => setFecha(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <button
                                className="btn btn-primary w-100"
                                onClick={buscarCobranza}
                                disabled={!fecha || loading}
                            >
                                {loading ? "Buscando..." : "Buscar"}
                            </button>
                        </div>
                        <div className="col-md-4">
                            {!isLiquidado && resultados.length > 0 && (
                                <button
                                    className="btn btn-warning text-dark w-100 fw-bold border border-warning"
                                    onClick={() => {
                                        gdAlertify.confirm({
                                            title: `¿Liquidar periodo ${fecha}?`,
                                            message: "Se generará una foto exacta inmodificable de toda la cobranza actual. Ya no podrás agregar ajustes o adelantos a este mes.",
                                            onConfirm: async () => {
                                                gdAlertify.showLoader();
                                                try {
                                                    await axiosInstance.post('/acuerdo-mutuo/cobranza/liquidar', { periodo: fecha });
                                                    gdAlertify.notiSuccess("El periodo ha sido cerrado con éxito.");
                                                    await buscarCobranza();
                                                } catch (e) {
                                                    gdAlertify.notiError("No se pudo liquidar este periodo.");
                                                } finally {
                                                    gdAlertify.hideLoader();
                                                }
                                            },
                                            onCancel: () => {}
                                        });
                                    }}
                                >
                                    <i className="bi bi-safe2 me-2"></i>
                                    Liquidar Mes
                                </button>
                            )}
                            {isLiquidado && (
                                <button
                                    className="btn btn-outline-warning text-dark w-100 fw-bold"
                                    onClick={reliquidarMes}
                                    title="Recalcula todos los pacientes del mes liquidado con los datos y reglas actuales"
                                >
                                    <i className="bi bi-arrow-repeat me-2"></i>
                                    Reliquidar Mes
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-3">
                <ul className="nav nav-tabs">
                    <li className="nav-item">
                        <button className={`nav-link ${vistaActiva === "general" ? "active text-dark fw-bold" : "text-secondary"}`} onClick={() => setVistaActiva("general")}>
                            Liquidación y Notificaciones
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${vistaActiva === "facturas" ? "active text-dark fw-bold" : "text-secondary"}`} onClick={() => setVistaActiva("facturas")}>
                            Facturas y Saldos
                        </button>
                    </li>
                </ul>

                {vistaActiva === "general" && (
                    <div className="card border-top-0 rounded-top-0">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0 text-muted">Resultados del Mes</h5>
                            </div>
                            <div className="mt-3">
                                <GdTable
                                    columns={columns}
                                    data={resultadosFiltrados}
                                    stickyHeader={true}
                                    height={600}
                                    headerHeight={50}
                                    showCsvExport={true}
                                    exportFilename={`cobranza-${fecha || "mes"}.xlsx`}
                                    initialSort={[0, "asc"]}
                                    loading={loading}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {vistaActiva === "facturas" && (
                    <CobranzaFacturas fecha={fecha} />
                )}
            </div>

            <CobranzaDetalle
                visible={showDetalle}
                onClose={() => setShowDetalle(false)}
                periodo={fecha}
                isLiquidado={isLiquidado}
                pacienteID={detallePacienteID}
                pacienteNombre={detallePacienteNombre}
                pacienteData={resultadosFiltrados.find(r => r.paciente_id === detallePacienteID)}
                modoNotificacion={modoNotificacion}
                onNotificacionEnviada={(tipoStr, meta = {}) => {
                    setResultados(prev => prev.map(row => {
                        if (row.paciente_id === detallePacienteID) {
                            return {
                                ...row,
                                numero_notificacion: tipoStr,
                                fecha_ultima_notificacion: new Date().toISOString(),
                                reajuste_avisado: meta.reajusteAvisado ? true : row.reajuste_avisado,
                                fecha_aviso_reajuste: meta.reajusteAvisado ? new Date().toISOString() : row.fecha_aviso_reajuste,
                                reajuste_apto_aviso: meta.reajusteAvisado ? false : row.reajuste_apto_aviso
                            };
                        }
                        return row;
                    }));
                }}
            />

            <CobranzaAjusteModal 
                visible={showAjusteModal}
                onClose={() => setShowAjusteModal(false)}
                pacienteID={detallePacienteID}
                pacienteNombre={detallePacienteNombre}
                periodo={fecha}
                cargosIniciales={detalleCargos}
                descuentosIniciales={detalleDescuentos}
                observacionInicial={detalleObservacionAjuste}
                onSuccess={() => {
                    buscarCobranza();
                }}
            />

            <CobranzaAdelantoModal
                visible={showAdelantoModal}
                onClose={() => setShowAdelantoModal(false)}
                pacienteID={detallePacienteID}
                pacienteNombre={detallePacienteNombre}
                periodo={fecha}
                onSuccess={() => {
                    buscarCobranza(); // Recargamos para que traiga los nuevos números y turnos adelantados
                }}
            />

            <CartolaPacienteModal
                show={showCartolaModal}
                handleClose={() => setShowCartolaModal(false)}
                paciente={selectedPacienteCartola}
            />

            <CobranzaReajusteModal
                visible={showReajusteModal}
                onClose={() => setShowReajusteModal(false)}
                pacientes={reajustePacientes}
                onSuccess={() => buscarCobranza()}
            />

            <NotificarCobranzaModal
                visible={showNotificarCorreo}
                onClose={() => setShowNotificarCorreo(false)}
                periodo={fecha}
            />
        </div>
    );
};

export default Cobranza;
